import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import type { ConnectedProjectDto, UserRoleTypeCommunityGroupPermissionsEntityRequest } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Form } from "@/components/Form/Form";
import { FormErrorWrapper } from "@/components/Form/FormErrorWrapper";
import { FormGroupIconPicker } from "@/components/Form/FormGroupIconPicker";
import { FormImageInput } from "@/components/Form/FormImageInput";
import { FormInput } from "@/components/Form/FormInput";
import { FormMultiSelect } from "@/components/Form/FormMultiSelect";
import { FormRadio, FormRadioGroup } from "@/components/Form/FormRadio";
import { FormSelect } from "@/components/Form/FormSelect";
import { FormTextArea } from "@/components/Form/FormTextArea";
import type { GroupIconType } from "@/components/GroupIcon/GroupIcon";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PermissionSelector } from "@/components/PermissionSelector/PermissionSelector";
import { Button } from "@/design-system/Button";
import { Checkbox } from "@/design-system/Checkbox";
import { FormField } from "@/design-system/FormField";
import { Notice } from "@/design-system/Notice";
import { createImageSizeRule, createImageTypeRule, createRequiredStringRule } from "@/helpers/rules";
import type { FormPermissions } from "@/helpers/system-groups/permissions";
import { sortAlphabetically } from "@/helpers/util";
import { useTranslation } from "@/translations";

export interface PlatformGroupFormProps {
  isEditMode: boolean;
  projects: ConnectedProjectDto[];
  defaultValues: FormValues;
  onSubmit: (data: FormValues) => Promise<unknown>;
  isSubmitting: boolean;
}
const MAX_LENGTH = {
  NAME: 40,
  DESCRIPTION: 400,
};
export function PlatformGroupForm({
  isEditMode,
  projects,
  defaultValues,
  onSubmit,
  isSubmitting,
}: PlatformGroupFormProps): React.ReactNode {
  const { t } = useTranslation();
  const [isAllProjects, setIsAllProjects] = useState<boolean>(
    isEditMode ? defaultValues.projects.length === projects.length : true,
  );
  const formMethods = useForm<FormValues>({
    defaultValues,
  });
  const postingLevel = useWatch({
    name: "postingLevel",
    control: formMethods.control,
  });
  const permissions = useWatch({
    name: "permissions",
    control: formMethods.control,
  });
  const audience = useMemo(() => {
    if (postingLevel === "project") {
      return ["project"];
    } else if (postingLevel === "projectConnection") {
      return ["projectConnection"];
    } else {
      return ["project", "projectConnection"];
    }
  }, [postingLevel]);
  useEffect(() => {
    if (postingLevel === "project") {
      formMethods.setValue("audience", "project");
    } else if (postingLevel === "projectConnection") {
      formMethods.setValue("audience", "projectConnection");
    }
  }, [formMethods, postingLevel]);
  useEffect(() => {
    if (isAllProjects) {
      formMethods.setValue("projects", projects);
    }
  }, [formMethods, projects, isAllProjects]);

  const title = isEditMode ? t("page.platform-groups.edit.title") : t("page.platform-groups.create.title");

  const permissionFields = [
    { key: "canCreatePost", label: t("page.platform-groups.form.permissions.can-create-post") },
    { key: "canCreateAnnouncement", label: t("page.platform-groups.form.permissions.can-create-announcement") },
    { key: "canListPosts", label: t("page.platform-groups.form.permissions.can-list-posts") },
    { key: "canCommentPost", label: t("page.platform-groups.form.permissions.can-comment") },
  ];

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={title}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.portfolio.title"),
            },
            {
              name: title,
            },
          ]}
        />
      }
    >
      <Form.Root formMethods={formMethods} onSubmit={onSubmit}>
        <Form.Container>
          <Form.Body>
            <FormField label={t("page.platform-groups.form.type.label")}>
              <FormErrorWrapper<FormValues> name="type">
                <FormRadioGroup>
                  <FormRadio<FormValues>
                    name="type"
                    value="interest"
                    label={t("page.platform-groups.list.type.interest-group")}
                    rules={{
                      validate: {
                        required: createRequiredStringRule(t, "page.platform-groups.form.name.label"),
                      },
                    }}
                    disabled={isEditMode}
                  />
                  <FormRadio<FormValues>
                    name="type"
                    value="helpCategory"
                    label={t("page.platform-groups.list.type.help-category")}
                    rules={{
                      validate: {
                        required: createRequiredStringRule(t, "page.platform-groups.form.name.label"),
                      },
                    }}
                    disabled={isEditMode}
                  />
                </FormRadioGroup>
              </FormErrorWrapper>
            </FormField>
            <FormField label={t("page.platform-groups.form.name.label")} required>
              <FormInput<FormValues>
                name="name"
                rules={{
                  validate: {
                    required: createRequiredStringRule(t, "page.platform-groups.form.name.label"),
                  },
                  maxLength: {
                    message: t("components.form.error.max-length", {
                      length: MAX_LENGTH.NAME,
                    }),
                    value: MAX_LENGTH.NAME,
                  },
                }}
                placeholder={t("page.platform-groups.form.name.placeholder")}
              />
            </FormField>
            <FormField label={t("page.platform-groups.form.description.label")} required>
              <FormTextArea<FormValues>
                name="description"
                rules={{
                  validate: {
                    required: createRequiredStringRule(t, "page.platform-groups.form.description.label"),
                  },
                  maxLength: {
                    message: t("components.form.error.max-length", {
                      length: MAX_LENGTH.DESCRIPTION,
                    }),
                    value: MAX_LENGTH.DESCRIPTION,
                  },
                }}
                placeholder={t("page.platform-groups.form.description.placeholder")}
                compactHeight
              />
            </FormField>
            <FormField label={t("page.platform-groups.form.icon.label")} required>
              <FormGroupIconPicker<FormValues>
                data-testid="group-icon"
                name="icon"
                rules={{
                  required: t("components.form.error.required", {
                    inputName: t("page.platform-groups.form.icon.label"),
                  }),
                }}
              />
            </FormField>
            <FormField label={t("page.platform-groups.form.image.label")} required>
              <FormImageInput<FormValues, "image">
                name="image"
                rules={{
                  required: t("components.form.error.required", {
                    inputName: t("page.platform-groups.form.image.label"),
                  }),
                  validate: {
                    size: createImageSizeRule({
                      t,
                    }),
                    type: createImageTypeRule({
                      t,
                    }),
                  },
                }}
              />
            </FormField>
            <FormField label={t("page.platform-groups.form.projects.label")} required>
              <Checkbox
                name="allProjects"
                onChange={(e) => setIsAllProjects(e.target.checked)}
                checked={isAllProjects}
                disabled={isEditMode}
                label={t("page.platform-groups.form.projects.checkbox")}
              />
              <FormMultiSelect<FormValues, "projects">
                name="projects"
                placeholder={t("page.platform-groups.form.projects.placeholder")}
                items={projects || []}
                searchableFieldSelector={(project) => [project.name, project.owner, project.maintenance]}
                renderOption={(project) => project.name}
                keySelector={(project) => project.id}
                sortSelectedItems={sortAlphabetically}
                rules={{
                  required: t("components.form.error.required", {
                    inputName: t("page.platform-groups.form.projects.label"),
                  }),
                }}
                disabled={isAllProjects || isEditMode}
              />
            </FormField>
            <FormField
              label={t("page.platform-groups.form.posting-level.label")}
              tooltip={t("page.platform-groups.form.posting-level.tooltip")}
            >
              <FormSelect<FormValues, string>
                name="postingLevel"
                placeholder={t("page.platform-groups.form.posting-level.placeholder")}
                items={["project", "projectConnection", "projectAndProjectConnection"]}
                keySelector={(x) => x}
                renderOption={(x) => {
                  switch (x) {
                    case "project":
                      return t("page.platform-groups.form.posting-level.options.project");
                    case "projectConnection":
                      return t("page.platform-groups.form.posting-level.options.connection");
                    case "projectAndProjectConnection":
                      return t("page.platform-groups.form.posting-level.options.both");
                  }
                }}
                rules={{
                  required: t("components.form.error.required", {
                    inputName: t("page.platform-groups.form.posting-level.label"),
                  }),
                }}
                disabled={isEditMode}
              />
            </FormField>
            <FormField
              label={t("page.platform-groups.form.audience.label")}
              tooltip={t("page.platform-groups.form.audience.tooltip")}
            >
              <FormSelect<FormValues, string>
                name="audience"
                placeholder={t("page.platform-groups.form.audience.placeholder")}
                items={audience}
                keySelector={(x) => x}
                renderOption={(x) => {
                  switch (x) {
                    case "project":
                      return t("page.platform-groups.form.audience.options.project");
                    case "projectConnection":
                      return t("page.platform-groups.form.audience.options.connection");
                  }
                }}
                rules={{
                  required: t("components.form.error.required", {
                    inputName: t("page.platform-groups.form.audience.label"),
                  }),
                }}
                disabled={isEditMode && postingLevel !== "projectAndProjectConnection"}
              />
            </FormField>
            <FormField
              label={t("component.permission-section.title")}
              description={t("component.permission-section.description", {
                permissionTarget: t("common.entity.group", { count: 1 }).toLowerCase(),
              })}
            >
              <PermissionSelector
                defaultPermissions={permissions}
                fields={permissionFields}
                onSave={(newPermissions) => formMethods.setValue("permissions", newPermissions)}
              />
            </FormField>
            <aside className="w-fit flex-0">
              {isSubmitting && <Notice variant="warning" message={t("common.label.wait-and-not-exit-page")} />}
            </aside>
          </Form.Body>
          <Form.Footer
            renderPrimaryAction={() => (
              <Button type="submit" isLoading={isSubmitting}>
                {isEditMode ? t("common.action.save") : t("common.action.create")}
              </Button>
            )}
          />
        </Form.Container>
      </Form.Root>
    </DocumentPage>
  );
}
export interface FormValues {
  type: "interest" | "helpCategory" | "realEstate";
  name: string;
  description: string;
  icon?: GroupIconType;
  image?: FormImage[];
  projects: ConnectedProjectDto[];
  permissions: FormPermissions<UserRoleTypeCommunityGroupPermissionsEntityRequest>;
  postingLevel: "project" | "projectConnection" | "projectAndProjectConnection";
  audience: "project" | "projectConnection";
}
